/* eslint-env browser */
// Add/edit a partner NGO (wave 3, W2_Portal_Settings look: one card, one
// label above each field, the error under the field, one Save button). On
// Add, editNgo returns the new push key so a chosen image can be uploaded
// against it (Storage path ngos/<id>) and the record updated with the
// download URL in one flow. The record and the calls are the old page's:
// { name (trimmed), description, image, active, order: Number(order) || 0 }
// plus createdAt / createdBy (kept on edit, set on add), editNgo 'Update' /
// 'Add', uploadNgoImage, then fetchNgos() and back to the list.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import VolunteerActivismOutlinedIcon from '@mui/icons-material/VolunteerActivismOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { api } from 'common';
import CircularLoading from '../components/CircularLoading';
import { showToast, InlineMessage } from '../components/feedback';
import { BackButton, PageHeader, SectionCard, FieldLabel, FieldError } from '../components/modern';
import { tokens as c } from '../theme';

const EditNgo = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const settings = useSelector((state) => state.settingsdata.settings);
  const ngos = useSelector((state) => state.ngodata.ngos);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [nameError, setNameError] = useState(null);
  const { editNgo, fetchNgos, uploadNgoImage } = api;
  const [data, setData] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const { state } = useLocation();
  // The list loads again when the page opens; what the person already typed
  // is kept when it arrives.
  const touched = useRef(false);

  // NGO list is fetched on mount (no longer pre-fetched at login).
  useEffect(() => {
    dispatch(fetchNgos());
  }, [dispatch, fetchNgos]);

  useEffect(() => {
    if (touched.current) return;
    if (id) {
      if (ngos) {
        const ngoData = ngos.filter((item) => item.id === id.toString())[0];
        if (!ngoData) {
          navigate('/404');
          return;
        }
        setData(ngoData);
      }
    } else {
      setData({
        name: '',
        description: '',
        image: '',
        active: true,
        order: 0,
      });
    }
  }, [ngos, id, navigate]);

  const previewUrl = useMemo(() => (imageFile ? URL.createObjectURL(imageFile) : null), [imageFile]);
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );

  const change = (patch) => {
    touched.current = true;
    setData((prev) => ({ ...prev, ...patch }));
  };

  const goBack = () => {
    navigate('/ngos', { state: { pageNo: state?.pageNo } });
  };

  const handleSubmit = async () => {
    if (loading) return;
    setFormError(null);
    if (!settings.AllowCriticalEditsAdmin) {
      showToast.warning(t('demo_mode'));
      return;
    }
    if (!data.name || !data.name.trim()) {
      setNameError(t('ngo_name_required', { defaultValue: 'NGO name is required' }));
      const el = document.getElementById('en-name');
      if (el) el.focus();
      return;
    }
    setLoading(true);
    try {
      const record = {
        name: data.name.trim(),
        description: data.description || '',
        image: data.image || '',
        active: data.active === true,
        order: Number(data.order) || 0,
      };
      if (id) {
        // editNgo 'Update' is a whole-node set — carry the audit
        // fields through or the first edit would erase them.
        if (data.createdAt !== undefined) record.createdAt = data.createdAt;
        if (data.createdBy !== undefined) record.createdBy = data.createdBy;
        let image = record.image;
        if (imageFile) {
          image = await uploadNgoImage(id, imageFile);
        }
        await dispatch(editNgo({ ...record, id, image }, 'Update'));
      } else {
        record.createdAt = Date.now();
        record.createdBy = auth?.profile?.uid || '';
        const key = await dispatch(editNgo(record, 'Add'));
        if (imageFile && key) {
          const image = await uploadNgoImage(key, imageFile);
          await dispatch(editNgo({ ...record, id: key, image }, 'Update'));
        }
      }
      dispatch(fetchNgos());
      showToast.success(t('saved', { defaultValue: 'Saved' }));
      goBack();
    } catch (error) {
      setFormError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!data) return <CircularLoading />;

  const pictureSrc = previewUrl || data.image;

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <BackButton onClick={goBack} />
      <PageHeader title={id ? t('edit_ngo', { defaultValue: 'Edit NGO' }) : t('add_ngo', { defaultValue: 'Add NGO' })} />
      {formError ? (
        <InlineMessage kind="error" onClose={() => setFormError(null)}>
          {formError}
        </InlineMessage>
      ) : null}
      <SectionCard id="en-card" icon={<VolunteerActivismOutlinedIcon />} hue="success" title={t('ngo', { defaultValue: 'NGO' })}>
        {/* One column on a phone, two from md (as before), three from lg; the
            description and the picture take the whole row. */}
        <Box
          sx={{
            display: 'grid',
            columnGap: 2.5,
            rowGap: 2.25,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
            mt: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel htmlFor="en-name">{t('ngo_name', { defaultValue: 'Name' })}</FieldLabel>
            <TextField
              id="en-name"
              fullWidth
              value={data.name || ''}
              disabled={loading}
              error={!!nameError}
              onChange={(e) => {
                change({ name: e.target.value });
                if (nameError && e.target.value.trim()) setNameError(null);
              }}
              inputProps={{ autoComplete: 'off', 'aria-describedby': nameError ? 'en-name-error' : undefined }}
            />
            <FieldError id="en-name-error">{nameError}</FieldError>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel htmlFor="en-order">{t('ngo_order', { defaultValue: 'Display order' })}</FieldLabel>
            <TextField
              id="en-order"
              fullWidth
              value={data.order ?? 0}
              disabled={loading}
              onChange={(e) => change({ order: e.target.value })}
              inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
              sx={{ '& input': { fontVariantNumeric: 'tabular-nums' } }}
            />
          </Box>
          <Box sx={{ minWidth: 0, gridColumn: '1 / -1' }}>
            <FieldLabel htmlFor="en-description">{t('ngo_description', { defaultValue: 'Description' })}</FieldLabel>
            <TextField
              id="en-description"
              fullWidth
              multiline
              minRows={4}
              value={data.description || ''}
              disabled={loading}
              onChange={(e) => change({ description: e.target.value })}
            />
          </Box>
          <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, minHeight: 44 }}>
            <FieldLabel htmlFor="en-active" sx={{ mb: 0 }}>
              {t('ngo_active', { defaultValue: 'Active' })}
            </FieldLabel>
            <Switch id="en-active" checked={data.active === true} disabled={loading} onChange={(e) => change({ active: e.target.checked })} />
          </Box>
          <Box sx={{ minWidth: 0, gridColumn: '1 / -1' }}>
            <FieldLabel htmlFor="en-image">{t('ngo_image', { defaultValue: 'Logo / Image' })}</FieldLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              {pictureSrc ? (
                <Box
                  component="img"
                  alt={t('ngo_image', { defaultValue: 'Logo / Image' })}
                  src={pictureSrc}
                  sx={{ width: 120, height: 120, borderRadius: '12px', objectFit: 'cover', border: `1px solid ${c.OUTLINE}` }}
                />
              ) : null}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                <Button variant="outlined" color="neutral" component="label" startIcon={<UploadFileOutlinedIcon />} disabled={loading}>
                  {imageFile || data.image
                    ? t('car_types_image_other', { defaultValue: 'Choose another picture' })
                    : t('car_types_image_choose', { defaultValue: 'Choose a picture' })}
                  <input
                    id="en-image"
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0];
                      if (file) {
                        touched.current = true;
                        setImageFile(file);
                      }
                      e.target.value = '';
                    }}
                  />
                </Button>
                {imageFile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, color: c.TEXT_3, overflowWrap: 'anywhere' }}>{imageFile.name}</Typography>
                    <Button variant="text" color="neutral" onClick={() => setImageFile(null)} disabled={loading}>
                      {t('car_types_image_remove', { defaultValue: 'Remove' })}
                    </Button>
                  </Box>
                ) : null}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckRoundedIcon />}
            sx={{ minWidth: { xs: '100%', sm: 200 } }}
          >
            {loading ? t('form_saving', { defaultValue: 'Saving…' }) : t('save', { defaultValue: 'Save' })}
          </Button>
        </Box>
      </SectionCard>
    </Box>
  );
};

export default EditNgo;
